/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * TrialBanner
 *
 * Persistent banner shown at the top of the protected app shell while the
 * user is on a trial subscription. Renders nothing for non-trial users.
 *
 * Behaviour:
 *  - Polls /subscriptions/me/subscription on mount and on `paxia:subscription-changed` events.
 *  - Shows days remaining (or "ends today" / "in grace") with an Upgrade CTA.
 *  - Dismissible per session (sessionStorage), but reappears each new session and
 *    is non-dismissible during the final 3 days for visibility.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Clock, X, Sparkles } from "lucide-react";
import { getMySubscription } from "../../api/subscriptions";

const DISMISS_KEY = "paxia:trial-banner-dismissed";

function daysBetween(future) {
  if (!future) return null;
  try {
    const ms = new Date(future).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86_400_000));
  } catch {
    return null;
  }
}

export default function TrialBanner() {
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  const refresh = useCallback(async () => {
    try {
      const data = await getMySubscription();
      setSub(data?.status === "none" ? null : data);
    } catch {
      setSub(null);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener("paxia:subscription-changed", onChange);
    return () => window.removeEventListener("paxia:subscription-changed", onChange);
  }, [refresh]);

  if (!sub || !sub.is_trial) return null;

  const inGrace = sub.status === "grace";
  const daysLeft = daysBetween(sub.trial_ends_at || sub.current_period_end);
  const isUrgent = inGrace || (typeof daysLeft === "number" && daysLeft <= 3);
  const trialName = sub.tier_name || "trial";

  if (dismissed && !isUrgent) return null;

  const handleDismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  let label;
  if (inGrace) {
    label = "Your trial has ended — you're in a short grace period.";
  } else if (daysLeft === 0) {
    label = "Your free trial ends today.";
  } else if (daysLeft === 1) {
    label = "Your free trial ends tomorrow.";
  } else if (typeof daysLeft === "number") {
    label = `Your free ${trialName} trial ends in ${daysLeft} days.`;
  } else {
    label = `You're on a free ${trialName} trial.`;
  }

  return (
    <div
      role="status"
      className={`flex items-center justify-between gap-3 px-3 py-1.5 text-xs font-medium border-b ${
        isUrgent
          ? "bg-amber-500/15 border-amber-500/40 text-amber-100"
          : "bg-[#4FA9A1]/10 border-[#4FA9A1]/30 text-[#A6E0DB]"
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Clock className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Link
          to="/subscription"
          className="inline-flex items-center gap-1 rounded-md bg-[#4FA9A1] hover:bg-[#3e938b] text-[#0F1826] px-2.5 py-1 text-[11px] font-semibold transition"
        >
          <Sparkles className="w-3.5 h-3.5" />
          Upgrade
        </Link>
        {!isUrgent && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss trial banner"
            className="rounded-md p-1 hover:bg-white/10 transition"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
